import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type Child, type EventInput, type LoggedEvent, type MeResponse, type Summary } from '../api';
import { messageFor } from '../App';
import { AssistantBar } from './AssistantBar';
import { ActivityLog, type ActivityLogHandle } from './ActivityLog';
import { EventForm } from './EventForm';

/**
 * The week at a glance, laid out like a calendar.
 *
 * Each child is a "calendar" you can toggle, because the question a parent actually has is
 * "what has each of them done this week", not "what happened". A shared lesson appears once
 * but is striped with every child who took part — splitting it into one block per child
 * would imply the hour was divided between them, which is exactly what it was not.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
/** Pixels per hour. Enough that a 30-minute block still holds a subject label. */
const HOUR_PX = 46;

export function CalendarView({ me, onChanged }: { me: MeResponse; onChanged: () => void }) {
  const kids = useMemo(() => me.children ?? [], [me.children]);
  const subjects = me.subjects ?? [];

  const [weekStart, setWeekStart] = useState(() => startOfWeek(localToday(me)));
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const [events, setEvents] = useState<LoggedEvent[]>([]);
  const [summary, setSummary] = useState<Summary>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<LoggedEvent | undefined>();
  const activity = useRef<ActivityLogHandle>(null);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const from = days[0] as string;
  const to = days[6] as string;

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [log, totals] = await Promise.all([
        api.events({ from, to }),
        api.summary({ from, to }),
      ]);
      setEvents(log.events);
      setSummary(totals);
      setError(undefined);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const colorById = useMemo(
    () => new Map(kids.map((child) => [child.childId, child.color])),
    [kids],
  );
  const nameById = useMemo(
    () => new Map(kids.map((child) => [child.childId, child.name])),
    [kids],
  );

  const visible = useMemo(
    () => events.filter((event) => event.childIds.some((id) => !hidden.has(id))),
    [events, hidden],
  );

  // Fit the grid to the day actually recorded, with a sane default for an empty week.
  const [startHour, endHour] = useMemo(() => {
    if (visible.length === 0) return [7, 18];
    let lo = 24;
    let hi = 0;
    for (const event of visible) {
      lo = Math.min(lo, Math.floor(minutes(event.startTime) / 60));
      hi = Math.max(hi, Math.ceil(minutes(event.endTime) / 60));
    }
    return [Math.max(0, Math.min(lo, 8)), Math.min(24, Math.max(hi, 17))];
  }, [visible]);

  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const gridHeight = hours.length * HOUR_PX;

  function toggleChild(childId: string) {
    setHidden((prior) => {
      const next = new Set(prior);
      if (next.has(childId)) next.delete(childId);
      else next.add(childId);
      return next;
    });
  }

  const todayLocal = localToday(me);

  return (
    <>
      <div className="panel compact">
        <AssistantBar
          me={me}
          compact
          onDone={() => {
            void load();
            onChanged();
            activity.current?.reload();
          }}
        />
        <ActivityLog
          ref={activity}
          me={me}
          inline
          limit={12}
          onChanged={() => {
            void load();
            onChanged();
          }}
        />
      </div>

      {error && <div className="notice error">{error}</div>}

      {kids.length === 0 && (
        <div className="notice info">
          Add a student in Settings and the week will start filling in. You can also just say
          “add my daughter Ada, she’s in second grade” above.
        </div>
      )}

      {/* Calendar first, with its controls inline, so the week is visible without scrolling.
          The activity rail sits beside it on a wide screen and below it otherwise. */}
      <div className="panel calendar-panel">
          <div className="week-bar">
            <div className="week-nav">
              <button onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous week">
                ‹
              </button>
              <button onClick={() => setWeekStart(startOfWeek(todayLocal))}>Today</button>
              <button onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Next week">
                ›
              </button>
            </div>
            <strong className="week-range">{formatRange(from, to)}</strong>
            {busy && <span className="muted">loading…</span>}
            <span className="spacer" />
            {kids.map((child) => (
              <button
                key={child.childId}
                className="legend-item"
                aria-pressed={!hidden.has(child.childId)}
                onClick={() => toggleChild(child.childId)}
                title={hidden.has(child.childId) ? `Show ${child.name}` : `Hide ${child.name}`}
              >
                <span
                  className="swatch"
                  style={{
                    background: hidden.has(child.childId) ? 'transparent' : child.color,
                    borderColor: child.color,
                  }}
                />
                {child.name}
              </button>
            ))}
          </div>

          <div className="calendar" style={{ gridTemplateColumns: `56px repeat(7, 1fr)` }}>
            <div className="cal-corner" />
            {days.map((day) => (
              <div key={day} className={`cal-head${day === todayLocal ? ' today' : ''}`}>
                <div className="dow">{dayName(day)}</div>
                <div className="dom">{Number(day.slice(8, 10))}</div>
              </div>
            ))}

            <div className="cal-gutter" style={{ height: gridHeight }}>
              {hours.map((h) => (
                <div key={h} className="cal-hour-label" style={{ height: HOUR_PX }}>
                  {formatHour(h)}
                </div>
              ))}
            </div>

            {days.map((day) => (
              <div key={day} className="cal-day" style={{ height: gridHeight }}>
                {hours.map((h) => (
                  <div key={h} className="cal-hour-line" style={{ height: HOUR_PX }} />
                ))}
                {layout(visible.filter((event) => event.date === day)).map(
                  ({ event, column, columns }) => {
                    const top = ((minutes(event.startTime) - startHour * 60) / 60) * HOUR_PX;
                    const height = Math.max(16, (event.durationMinutes / 60) * HOUR_PX - 2);
                    const shown = event.childIds.filter((id) => !hidden.has(id));
                    const primary = colorById.get(shown[0] ?? '') ?? '#888888';

                    return (
                      <div
                        key={event.eventId}
                        role="button"
                        tabIndex={0}
                        onClick={() => setEditing(event)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') setEditing(event);
                        }}
                        className={`cal-event${event.verified ? '' : ' unverified'}`}
                        style={{
                          top,
                          height,
                          left: `calc(${(column / columns) * 100}% + 2px)`,
                          width: `calc(${100 / columns}% - 5px)`,
                          borderLeftColor: primary,
                          background: `color-mix(in srgb, ${primary} 14%, var(--panel))`,
                        }}
                        title={
                          `${event.startTime}–${event.endTime} · ` +
                          `${subjects.find((s) => s.id === event.subject)?.label ?? event.subject}\n` +
                          `${shown.map((id) => nameById.get(id) ?? '?').join(', ')}\n\n` +
                          `${event.description}` +
                          (event.verified ? '' : '\n\n(not yet confirmed)')
                        }
                      >
                        <div className="cal-stripes">
                          {shown.map((id) => (
                            <span key={id} style={{ background: colorById.get(id) ?? '#888' }} />
                          ))}
                        </div>
                        <div className="cal-event-body">
                          <div className="cal-subject">
                            {subjects.find((s) => s.id === event.subject)?.label ?? event.subject}
                          </div>
                          {height > 34 && <div className="cal-desc">{event.description}</div>}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            ))}
          </div>
      </div>

      {editing && (
        <EventEditor
          me={me}
          event={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            void load();
            onChanged();
          }}
        />
      )}

      {summary && summary.pace.length > 0 && (
        <div className="panel">
          <h2>Hours logged</h2>
          <p className="sub">
            School year {summary.schoolYear.from} to {summary.schoolYear.to} ·{' '}
            {summary.pace[0]?.weeksRemaining ?? 0} weeks left. The shaded part is where an
            even pace would have you by now. Weekly pace is a guide, not a rule — no state
            here requires hours in a given week, so a quiet week you make up later is fine.
          </p>

          {summary.pace.map((row) => (
            <div key={row.childId} className="pace-row">
              <div className="pace-name">
                <span className="swatch" style={{ background: row.color, borderColor: row.color }} />
                {row.name}
              </div>

              <div className="pace-bars">
                <PaceBar
                  label="Week"
                  logged={row.loggedThisWeekHours}
                  target={row.weeklyTargetHours}
                  expected={row.expectedThisWeekHours}
                  color={row.color}
                />
                <PaceBar
                  label="Year"
                  logged={row.loggedThisYearHours}
                  target={row.annualRequiredHours}
                  expected={row.expectedThisYearHours}
                  color={row.color}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * What an entry says, with editing behind a deliberate second step.
 *
 * Clicking a block on a calendar usually means "what is this", not "let me rewrite it", and
 * opening a form full of inputs makes a glance feel like a commitment — and puts a stray
 * click one keystroke away from altering a record. So the first view is read-only, dismissed
 * by clicking away or pressing Escape, and editing is a button.
 */
function EventEditor({
  me,
  event,
  onClose,
  onSaved,
}: {
  me: MeResponse;
  event: LoggedEvent;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const kids = me.children ?? [];
  // Only inferred entries need approving. Something the parent stated is already theirs.
  const needsConfirming = !event.verified && (event.source === 'import' || event.source === 'device');
  const [attributed, setAttributed] = useState<string[]>(event.childIds);
  const subject = me.subjects?.find((s) => s.id === event.subject);
  const names = event.childIds
    .map((id) => kids.find((c) => c.childId === id)?.name ?? 'removed student')
    .join(', ');

  async function guard(action: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
      onSaved();
    } catch (err) {
      setError(messageFor(err));
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="presentation"
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        ref={(node) => node?.focus()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
      >
        <h2>
          {subject?.label ?? event.subject}
          {subject?.core && <span className="pill core" style={{ marginLeft: 8 }}>core</span>}
        </h2>
        <p className="sub">
          {event.date} · {event.startTime}–{event.endTime} ·{' '}
          {(event.durationMinutes / 60).toFixed(2)} hrs ·{' '}
          {event.location === 'home' ? 'at home' : 'away'}
        </p>

        {needsConfirming && (
          <div className="notice warn">
            This was worked out from {event.source === 'import' ? 'a calendar entry' : 'a recording'},
            not from something you said. Check the students it counts for, then confirm it.
          </div>
        )}

        {error && <div className="notice error">{error}</div>}

        {editing ? (
          <EventForm
            subjects={me.subjects}
            kids={kids}
            initial={event}
            onCancel={() => setEditing(false)}
            onSave={async (changes) => {
              await api.updateEvent(event.date, event.eventId, {
                changes: changes as Partial<EventInput>,
                expectedRevision: event.revision,
                reason: 'Edited on the calendar',
              });
              onSaved();
            }}
          />
        ) : (
          <>
            <p style={{ margin: '0 0 10px' }}>{event.description}</p>
            <p className="muted" style={{ margin: 0 }}>
              {names} · recorded {sourceLabel(event.source)}
              {event.verified ? '' : ' · not yet confirmed by a parent'}
            </p>

            {needsConfirming && kids.length > 0 && (
              <div className="field" style={{ marginTop: 14 }}>
                <label>Counts for</label>
                <div className="legend">
                  {kids.map((child) => (
                    <button
                      key={child.childId}
                      className="legend-item"
                      aria-pressed={attributed.includes(child.childId)}
                      onClick={() =>
                        setAttributed((prior) =>
                          prior.includes(child.childId)
                            ? prior.filter((id) => id !== child.childId)
                            : [...prior, child.childId],
                        )
                      }
                    >
                      <span
                        className="swatch"
                        style={{
                          background: attributed.includes(child.childId)
                            ? child.color
                            : 'transparent',
                          borderColor: child.color,
                        }}
                      />
                      {child.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="modal-actions">
              {needsConfirming && (
                <button
                  className="primary"
                  disabled={busy || attributed.length === 0}
                  title={
                    attributed.length === 0
                      ? 'An entry has to count for at least one student'
                      : undefined
                  }
                  onClick={() =>
                    void guard(async () => {
                      // Narrowing the students is an ordinary edit, so it bumps the
                      // revision; the confirmation then has to present the new one.
                      const sameChildren =
                        attributed.length === event.childIds.length &&
                        attributed.every((id) => event.childIds.includes(id));

                      let revision = event.revision;
                      if (!sameChildren) {
                        const updated = await api.updateEvent(event.date, event.eventId, {
                          changes: { childIds: attributed },
                          expectedRevision: revision,
                          reason: 'Students corrected while confirming',
                        });
                        revision = updated.event.revision;
                      }
                      await api.verifyEvent(event.date, event.eventId, revision);
                    })
                  }
                >
                  Confirm
                </button>
              )}
              <button disabled={busy} onClick={() => setEditing(true)}>
                Edit
              </button>
              <span className="spacer" />
              <button
                className="danger"
                disabled={busy}
                onClick={() => {
                  // Confirm, not a prompt for prose. The audit trail already records who
                  // removed it and when, and typing a reason every time is friction that
                  // buys a field nobody fills in honestly.
                  if (!window.confirm('Delete this entry? It stays on the audit trail.')) return;
                  void guard(async () => {
                    await api.deleteEvent(event.date, event.eventId, 'Deleted on the calendar');
                  });
                }}
              >
                Delete
              </button>
              <button disabled={busy} onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function sourceLabel(source: string): string {
  return (
    {
      device: 'from a recording',
      manual: 'by hand',
      assistant: 'by the assistant',
      mcp: 'through the API',
      import: 'by import',
    }[source] ?? source
  );
}

/**
 * One bar: an outlined box the full width of the requirement, a light shade for where an
 * even pace would have you today, and a solid fill for what is actually logged.
 *
 * The box always spans the whole target so the endpoint is a fixed, visible thing rather
 * than something inferred from how full a bar looks. Scaling the box to whichever number
 * happened to be largest would make two children's bars mean different things, and make a
 * week in September look like a week in May.
 */
function PaceBar({
  label,
  logged,
  target,
  expected,
  color,
}: {
  label: string;
  logged: number;
  target: number;
  expected: number;
  color: string;
}) {
  const pct = (value: number) => Math.max(0, Math.min(100, (value / Math.max(target, 0.01)) * 100));
  const delta = logged - expected;
  const met = logged >= target;

  return (
    <div className="pace-bar">
      <span className="pace-label">{label}</span>

      <div className="pace-track" title={`${logged.toFixed(1)} of ${Math.round(target)} hrs`}>
        <span className="expected" style={{ width: `${pct(expected)}%` }} />
        <span className="done" style={{ width: `${pct(logged)}%`, background: color }} />
      </div>

      <div className="pace-figures">
        <div>
          <strong>{logged.toFixed(1)}</strong>
          {/* Targets read as whole hours; the decimal is noise on a number nobody hits exactly. */}
          <span className="muted"> / {Math.round(target)}</span>
        </div>
        <div className={`pace-delta${delta < -0.05 ? ' behind' : ''}`}>
          {met
            ? 'met'
            : delta < -0.05
              ? `${Math.abs(delta).toFixed(1)} behind`
              : `${delta.toFixed(1)} ahead`}
        </div>
      </div>
    </div>
  );
}

/**
 * Side-by-side packing for overlapping entries, the way a calendar does it. Without this,
 * two lessons at the same hour would sit on top of each other and one would be invisible —
 * which in an audit log is the difference between "we did two things" and "we did one".
 */
function layout(
  dayEvents: readonly LoggedEvent[],
): Array<{ event: LoggedEvent; column: number; columns: number }> {
  const sorted = [...dayEvents].sort(
    (a, b) => minutes(a.startTime) - minutes(b.startTime) || minutes(b.endTime) - minutes(a.endTime),
  );

  const out: Array<{ event: LoggedEvent; column: number; columns: number }> = [];
  let cluster: LoggedEvent[] = [];
  let clusterEnd = -1;

  const flush = () => {
    if (cluster.length === 0) return;
    const columnEnds: number[] = [];
    const placed = cluster.map((event) => {
      let column = columnEnds.findIndex((end) => end <= minutes(event.startTime));
      if (column === -1) {
        column = columnEnds.length;
        columnEnds.push(0);
      }
      columnEnds[column] = minutes(event.endTime);
      return { event, column };
    });
    for (const item of placed) out.push({ ...item, columns: columnEnds.length });
    cluster = [];
    clusterEnd = -1;
  };

  for (const event of sorted) {
    if (cluster.length > 0 && minutes(event.startTime) >= clusterEnd) flush();
    cluster.push(event);
    clusterEnd = Math.max(clusterEnd, minutes(event.endTime));
  }
  flush();
  return out;
}

function minutes(time: string): number {
  const [h, m] = time.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

function localToday(me: MeResponse): string {
  const zone = me.account?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Weeks run Sunday to Saturday, matching the calendar people already have on the wall. */
function startOfWeek(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return d.toISOString().slice(0, 10);
}

function addDays(date: string, days: number): string {
  return new Date(new Date(`${date}T00:00:00Z`).getTime() + days * DAY_MS)
    .toISOString()
    .slice(0, 10);
}

function dayName(date: string): string {
  return new Intl.DateTimeFormat(undefined, { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(`${date}T00:00:00Z`),
  );
}

function formatHour(hour: number): string {
  const suffix = hour < 12 ? 'am' : 'pm';
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}${suffix}`;
}

function formatRange(from: string, to: string): string {
  const fmt = (d: string, opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(undefined, { ...opts, timeZone: 'UTC' }).format(
      new Date(`${d}T00:00:00Z`),
    );
  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  return sameMonth
    ? `${fmt(from, { month: 'long', day: 'numeric' })} – ${fmt(to, { day: 'numeric', year: 'numeric' })}`
    : `${fmt(from, { month: 'short', day: 'numeric' })} – ${fmt(to, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

export type { Child };
